import React, { useState, useEffect } from 'react';
import { Box, Typography, Grid, CircularProgress, Alert, Button, Backdrop } from '@mui/material';
import { useTranslation } from 'react-i18next';
import { useQuery } from 'react-query';
import { createOrUpdateOrder, getMembershipsDashboard } from 'apis/controllers/membership';
import MembershipCard from './MembershipCard';
import TotalAmountCard from './TotalAmountCard'; // Import new component
import format from 'date-fns/format';
import { Add } from '@mui/icons-material';
import { useNavigate } from 'react-router';
import { useSnackbar } from 'notistack';
import { formatResponseMessage } from 'utils/formatResponseMessage';
import PaymentModal from './PaymentModal';
import { calculateTotalAmountData } from './calculationFunctions';
import { useForm, FormProvider } from 'react-hook-form';

const MemberHomePage = () => {
    const { t } = useTranslation();
    const [isLoading, setIsLoading] = useState(true);
    const [loadingOverlay, setLoadingOverlay] = useState(false);
    const [isError, setIsError] = useState(false);
    const [user, setUser] = useState(null);
    const [dependents, setDependents] = useState(null);
    const [totalAmountData, setTotalAmountData] = useState({ totalAmount: 0, amounts: [] });
    const [simpleDialogOpen, setSimpleDialogOpen] = React.useState(false);
    const navigate = useNavigate();
    const { enqueueSnackbar } = useSnackbar();
    const form = useForm();

    const fetchDashboard = async () => {
        try {
            const res = await getMembershipsDashboard();
            const data = res?.data;

            if (!data) throw new Error('No dashboard data available');

            // Handle setTotalAmountData separately to avoid stopping execution
            try {
                setTotalAmountData(calculateTotalAmountData(data));
            } catch (e) {
                console.error('Error setting total amount data:', e);
                enqueueSnackbar(t('ErrorRetrievingBasket'), { variant: 'error' });
            }

            const extractMembershipData = (person) => {
                if (!Array.isArray(person?.products)) return {};
                const selectedProduct = person.products.find((product) => product.selected);

                return {
                    membershipType: selectedProduct?.id || null,
                    membershipAmount: selectedProduct?.unitPrice || null,
                    membershipDiscount: selectedProduct?.discount ? selectedProduct.discount / 100 : null,
                    pendingPayment: !person?.isActiveMember && !!selectedProduct,
                    discountCode: selectedProduct?.discountCode,
                    discountComments: selectedProduct?.comments
                };
            };

            setUser({
                avatar: data.avatar,
                id: data.id,
                firstName: data.firstName || 'Unknown',
                lastName: data.lastName || '',
                dob: data.dob ? format(new Date(data.dob), 'yyyy-MM-dd') : null,
                roles:
                    data.personFunctions?.map((f) =>
                        f?.code1
                            ? `${f.functionDescription} - ${f.code1} - ${f?.organizationAbbr}`
                            : `${f.functionDescription}- ${f?.organizationAbbr}`
                    ) || [],
                isUser: true,
                membershipId: data.membershipId,
                isActiveMember: data.isActiveMember,
                expiresAt: data.membershipExpiresAt,
                products: Array.isArray(data.products) ? data.products : [],
                leadUserId: data.id,
                canRenew: data?.canRenew,
                gender: data?.gender,
                ...extractMembershipData(data)
            });

            setDependents(
                data.dependents?.map((d) => ({
                    avatar: d.avatar,
                    id: d.dependentId,
                    firstName: d.firstName,
                    lastName: d.lastName,
                    dob: d.dob ? format(new Date(d.dob), 'yyyy-MM-dd') : null,
                    roles:
                        d.functions?.map((f) =>
                            f?.code1
                                ? `${f.functionDescription} - ${f.code1} - ${f?.organizationAbbr}`
                                : `${f.functionDescription}- ${f?.organizationAbbr}`
                        ) || [],
                    isUser: false,
                    membershipId: d.membershipId,
                    isActiveMember: d.isActiveMember,
                    expiresAt: d.membershipExpiresAt,
                    products: Array.isArray(d.products) ? d.products : [],
                    leadUserId: data.id,
                    canRenew: d?.canRenew,
                    authCode: d?.authCode,
                    canInvite: d?.canInvite,
                    email: d?.email,
                    gender: d?.gender,
                    ...extractMembershipData(d)
                })) || []
            );
        } catch (e) {
            setIsError(true);
            console.error(t('ErrorFetchingDashboard'), e);
            enqueueSnackbar(t('ErrorRetrievingBasket'), { variant: 'error' });
        } finally {
            setIsLoading(false);
        }
    };

    useEffect(() => {
        fetchDashboard();
    }, []);

    if (isLoading) {
        return (
            <Box sx={{ display: 'flex', justifyContent: 'center', mt: 5 }}>
                <CircularProgress />
            </Box>
        );
    }

    if (isError) {
        return (
            <Box sx={{ display: 'flex', justifyContent: 'center', mt: 5 }}>
                <Alert severity="error">{t('Error fetching membership data')}</Alert>
            </Box>
        );
    }

    const handleMembershipRenewal = async (memberId, membershipType, amount, label, code = null, comments = null, discount = null) => {
        try {
            setLoadingOverlay(true);
            const res = await createOrUpdateOrder({
                Action: 'add',
                Order: [
                    {
                        PersonId: memberId,
                        ProductTypeId: membershipType,
                        DiscountCode: code,
                        Comments: comments
                    }
                ]
            });
            setLoadingOverlay(false);
            enqueueSnackbar(formatResponseMessage(res?.message), { variant: res?.status });
            if (res?.status !== 'success') return;

            // Update user or dependent membership details
            if (memberId === user.id) {
                setUser((prevUser) => ({
                    ...prevUser,
                    membershipType,
                    membershipAmount: amount,
                    membershipDiscount: discount,
                    pendingPayment: true,
                    discountCode: code,
                    discountComments: comments
                }));
            } else {
                setDependents((prevDependents) =>
                    prevDependents.map((dep) =>
                        dep.id === memberId
                            ? {
                                  ...dep,
                                  membershipType,
                                  membershipAmount: amount,
                                  membershipDiscount: discount,
                                  pendingPayment: true,

                                  discountCode: code,
                                  discountComments: comments
                              }
                            : dep
                    )
                );
            }

            // Update Total Payable Amount
            setTotalAmountData((prevData) => {
                const existingIndex = prevData.amounts.findIndex((entry) => entry.memberId === memberId);

                if (existingIndex !== -1) {
                    // If the member already exists, replace the old amount with the new one
                    const updatedAmounts = prevData.amounts.map((entry, index) =>
                        index === existingIndex
                            ? {
                                  ...entry,
                                  amount: discount > 0 ? amount - discount * amount : amount,
                                  label: `${label}`
                              }
                            : entry
                    );

                    // Recalculate totalAmount
                    const updatedTotal = updatedAmounts.reduce((sum, entry) => sum + entry.amount, 0);

                    return { totalAmount: updatedTotal, amounts: updatedAmounts };
                } else {
                    // If it's a new membership renewal, add a new entry
                    return {
                        totalAmount: prevData.totalAmount + (discount ? amount - discount * amount : amount),
                        amounts: [
                            ...prevData.amounts,
                            {
                                memberId,
                                label: `${label}`,
                                amount: discount ? amount - discount * amount : amount
                            }
                        ]
                    };
                }
            });
        } catch (e) {
            console.log(e);
            enqueueSnackbar(t('ErrorFound'), { variant: 'error' });
        }
    };

    const handleMembershipRemove = async (memberId, membershipType) => {
        setLoadingOverlay(true);
        const res = await createOrUpdateOrder({
            Action: 'delete',
            Order: [
                {
                    PersonId: memberId,
                    ProductTypeId: membershipType
                }
            ]
        });
        if (res?.status !== 'success') {
            setLoadingOverlay(false);
            enqueueSnackbar(formatResponseMessage(res?.message), { variant: res?.status });
            return;
        }

        // Remove membership details from user or dependent
        if (memberId === user?.id) {
            setUser((prevUser) => ({
                ...prevUser,
                membershipType: null,
                membershipAmount: null,
                pendingPayment: false
            }));
        } else {
            setDependents((prevDependents) =>
                prevDependents.map((dep) =>
                    dep.id === memberId ? { ...dep, membershipType: null, membershipAmount: null, pendingPayment: false } : dep
                )
            );
        }

        // Update Total Payable Amount
        setTotalAmountData((prevData) => {
            // Find the entry in the amounts array
            const updatedAmounts = prevData.amounts.filter((entry) => entry.memberId !== memberId);

            // Recalculate total amount
            const updatedTotal = updatedAmounts.reduce((sum, entry) => sum + entry.amount, 0);

            return { totalAmount: updatedTotal, amounts: updatedAmounts };
        });
        setLoadingOverlay(false);
        enqueueSnackbar(formatResponseMessage(res?.message), { variant: res?.status });
    };

    const handleClose = () => setSimpleDialogOpen(false);

    return (
        <FormProvider {...form}>
            <Box sx={{ p: 4 }}>
                <Backdrop sx={{ color: '#fff', zIndex: (theme) => theme.zIndex.drawer + 1 }} open={loadingOverlay}>
                    <CircularProgress color="inherit" />
                </Backdrop>
                <PaymentModal open={simpleDialogOpen} handleClose={handleClose} totalAmount={totalAmountData?.totalAmount} />
                <Grid container spacing={3} alignItems="flex-start">
                    {/* Welcome Message */}
                    <Grid item xs={12} md={8}>
                        <Typography variant="h3" fontWeight="bold" sx={{ mb: 1 }}>
                            {t('Welcome')}, {user?.firstName}! 👋
                        </Typography>
                        <Typography variant="body1" sx={{ mb: 3 }}>
                            {t('MemberDashboard.WelcomeMessage')}
                        </Typography>
                    </Grid>
                </Grid>

                <Grid container spacing={3} sx={{ mt: 2 }}>
                    {/* User Card */}
                    <Grid item xs={12} sm={12} md={6} lg={5}>
                        <MembershipCard
                            member={user}
                            onRenewMembership={handleMembershipRenewal}
                            onRemoveMembership={handleMembershipRemove}
                        />
                    </Grid>
                    {/* Total Amount Card at the Top Right */}
                    <Grid item xs={12} md={1} lg={3} />
                    <Grid item xs={12} sm={12} md={5} lg={4}>
                        <TotalAmountCard
                            totalAmountData={totalAmountData}
                            applyDiscount={
                                <Button onClick={() => {}} variant="contained" color="primary" fullWidth sx={{ mt: 2 }}>
                                    {t('ApplyDiscount')}
                                </Button>
                            }
                            paymentButton={
                                <Button
                                    onClick={() => {
                                        setSimpleDialogOpen(true);
                                    }}
                                    variant="contained"
                                    color="success"
                                    fullWidth
                                    sx={{ mt: 2 }}
                                >
                                    {t('ProceedToPayment')}
                                </Button>
                            }
                        />
                    </Grid>
                    {dependents?.length === 0 && (
                        <Grid item xs={12}>
                            <Button startIcon={<Add />} onClick={() => navigate('/profile')} sx={{ mt: 2 }} variant="contained">
                                {t('AddDependent')}
                            </Button>
                        </Grid>
                    )}
                    <Grid container item xs={12} spacing={1}>
                        {/* Dependents Section */}
                        {dependents?.length > 0 && (
                            <>
                                <Grid item xs={12}>
                                    <Typography variant="h4" fontWeight="bold">
                                        {t('Dependents')}
                                    </Typography>
                                    <Button startIcon={<Add />} onClick={() => navigate('/profile')} sx={{ mt: 2 }} variant="contained">
                                        {t('AddDependent')}
                                    </Button>
                                </Grid>

                                {dependents.map((dependent) => (
                                    <Grid item xs={12} sm={12} md={6} lg={4} key={dependent?.id}>
                                        <MembershipCard
                                            member={dependent}
                                            onRenewMembership={handleMembershipRenewal}
                                            onRemoveMembership={handleMembershipRemove}
                                            invalidate={fetchDashboard}
                                        />
                                    </Grid>
                                ))}
                            </>
                        )}
                    </Grid>
                </Grid>
            </Box>
        </FormProvider>
    );
};

export default MemberHomePage;
